/* global document */
import React from 'react';
import styled from 'styled-components';
import Resizer from 'react-image-file-resizer';
import dayjs from 'dayjs';
import {
  Button,
  Image,
  Input,
  InputNumber,
  Modal,
  Progress,
  Select,
  Spin,
  Switch,
  Tag,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CameraFilled,
  CameraOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  RightOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  WarningFilled
} from '@ant-design/icons';
import { describeRange, photoUrl, pressable } from 'src/constants/checklists';
import {
  assigneeIds,
  assigneeNames,
  canFillSection,
  isOversight,
  sectionOwnerList
} from 'src/constants/checklistAccess';
import BackButton from 'src/components/views/admin/checklists/BackButton';
import {
  allItems,
  isAnswered,
  itemNeedsAttention,
  markAllCandidates,
  outstandingItems,
  readingPasses,
  runProgress,
  runState,
  runSubtitle,
  sectionProgress
} from 'src/constants/checklistRuns';

const { TextArea } = Input;

const GREEN = '#2f8a4c';
const RED = '#c8442b';
const AMBER = '#b9770e';
const INK = '#1c1c1c';
const MUTED = '#5b6660';
const LINE = '#e3ebe5';

const TONE_COLOR = { success: GREEN, warning: AMBER, error: RED, default: MUTED };

const Shell = styled.div`
  max-width: 46em;
  margin: 0 auto;
  scroll-margin-top: 64px;
  /* Room for the fixed Complete bar on phones. */
  padding-bottom: 7em;
  @media (min-width: 992px) {
    padding-bottom: 1em;
  }

  /* Finger-sized fields. 16px text also stops iPhones zooming in on focus. */
  .ant-input,
  .ant-input-number-input {
    font-size: 16px;
  }
  textarea.ant-input {
    min-height: 44px;
    padding-top: 9px;
    padding-bottom: 9px;
  }
`;

// Pinned just under the phone's app header (56px tall) so the way back, the
// name and progress never scroll away. Kept to two short lines: anything taller
// covers the items being worked on. (Sticky only takes effect on phones; the
// desktop shell clips it.)
const Head = styled.div`
  position: sticky;
  top: 60px;
  z-index: 20;
  background: #fff;
  border-radius: 0 0 14px 14px;
  box-shadow: 0 6px 16px rgba(20, 40, 28, 0.08);
  padding: 0.55em 0.8em 0.5em;
  margin: 0 -0.25em 0.6em;

  .row {
    display: flex;
    align-items: center;
    gap: 0.6em;
  }
  .name {
    font-family: Nunito, sans-serif;
    font-weight: 800;
    font-size: 17px;
    line-height: 1.2;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sub {
    color: ${MUTED};
    font-size: 12.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .text {
    flex: 1;
    min-width: 0;
  }
`;

// Section name and "Show what's left": scrolls with the page.
const SubBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6em;
  flex-wrap: wrap;
  margin: 0 0 0.6em;
  padding: 0 0.15em;
  .crumb {
    flex: 1;
    min-width: 0;
    font-weight: 700;
    font-size: 14.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.5em;
    min-height: 44px;
    color: ${MUTED};
    font-size: 14px;
    white-space: nowrap;
    cursor: pointer;
  }
`;

const Banner = styled.div`
  border-radius: 12px;
  padding: 0.85em 1em;
  margin-bottom: 0.9em;
  background: ${p => (p.$tone === 'done' ? '#eaf6ee' : '#f4f7f5')};
  color: ${p => (p.$tone === 'done' ? '#1f5e34' : MUTED)};
  font-size: 14px;
  line-height: 1.5;
`;

// --- Overview --------------------------------------------------------------

const SectionList = styled.div`
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 14px;
  overflow: hidden;
`;

const SectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8em;
  padding: 0.85em 1em;
  min-height: 3.6em;
  cursor: pointer;
  border-top: 1px solid #eef2ef;
  &:first-child {
    border-top: 0;
  }
  &:hover {
    background: #f7faf8;
  }
  &:focus-visible {
    outline: 2px solid ${GREEN};
    outline-offset: -2px;
  }
  .title {
    flex: 1;
    min-width: 0;
    font-weight: 700;
    font-size: 15px;
  }
  .owner {
    display: block;
    color: ${MUTED};
    font-weight: 500;
    font-size: 12.5px;
    margin-top: 0.1em;
  }
  .count {
    font-variant-numeric: tabular-nums;
    color: ${MUTED};
    font-size: 13px;
    min-width: 3em;
    text-align: right;
  }
`;

// --- Section view ----------------------------------------------------------

const Card = styled.div`
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 14px;
  overflow: hidden;
`;

const MarkAll = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
  padding: 0.7em 1em;
  background: #f4f9f5;
  border-bottom: 1px solid ${LINE};
  font-size: 13px;
  color: ${MUTED};
  .hint {
    flex: 1;
    min-width: 0;
  }
`;

const Note = styled.div`
  padding: 0.65em 1em 0;
  color: ${MUTED};
  font-size: 13px;
`;

// One compact line per item; it only grows when it needs input.
const Row = styled.div`
  padding: 0.6em 0.75em 0.6em 1em;
  border-top: 1px solid #eef2ef;
  /* Clears the app header plus the pinned checklist header when jumping to an
     item that needs attention. */
  scroll-margin-top: 140px;
  transition: background 0.2s;
  &:first-of-type {
    border-top: 0;
  }
  &.problem {
    background: #fff4f1;
    box-shadow: inset 3px 0 0 ${RED};
  }
  .main {
    display: flex;
    align-items: center;
    gap: 0.6em;
    flex-wrap: wrap;
  }
  .text {
    flex: 1 1 11em;
    min-width: 0;
  }
  .label {
    font-size: 14.5px;
    line-height: 1.35;
  }
  .meta {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5em;
    margin-top: 0.15em;
    color: ${MUTED};
    font-size: 12px;
  }
  .critical {
    color: ${RED};
    font-weight: 700;
  }
  .controls {
    display: flex;
    align-items: center;
    gap: 0.35em;
    margin-left: auto;
  }
  .extra {
    margin-top: 0.55em;
  }
  .standard {
    color: ${MUTED};
    font-size: 13px;
    line-height: 1.45;
    margin-top: 0.4em;
  }
  .reason {
    color: ${RED};
    font-size: 12.5px;
    font-weight: 600;
    margin-top: 0.35em;
  }
  .by {
    color: #8a948e;
    font-size: 11.5px;
    margin-top: 0.3em;
  }
`;

// 40px round answer buttons: still a comfortable thumb target, but three fit
// beside the label on a phone.
const Dot = styled.button`
  all: unset;
  box-sizing: border-box;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: ${p => (p.$small ? '11px' : '16px')};
  cursor: pointer;
  border: 1.5px solid ${p => (p.$selected ? p.$color : '#d3ddd6')};
  background: ${p => (p.$selected ? p.$color : '#fff')};
  color: ${p => (p.$selected ? '#fff' : p.$color)};
  transition: background 0.12s, border-color 0.12s, transform 0.08s;
  &:active {
    transform: scale(0.92);
  }
  &:focus-visible {
    outline: 2px solid ${INK};
    outline-offset: 2px;
  }
  &[disabled] {
    cursor: default;
    opacity: ${p => (p.$selected ? 1 : 0.4)};
  }
`;

// Solid and thumb-sized so it can't be missed; outlined once a photo is in.
const PhotoButton = styled.button`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  min-height: 44px;
  padding: 0 1.2em;
  border-radius: 22px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  border: 1.5px solid ${GREEN};
  background: ${p => (p.$retake ? '#fff' : GREEN)};
  color: ${p => (p.$retake ? GREEN : '#fff')};
  box-shadow: ${p => (p.$retake ? 'none' : '0 3px 10px rgba(47, 138, 76, 0.3)')};
  transition: transform 0.08s;
  .anticon {
    font-size: 18px;
  }
  &:active {
    transform: scale(0.96);
  }
  &:focus-visible {
    outline: 2px solid ${INK};
    outline-offset: 2px;
  }
  @media (max-width: 575px) {
    flex: 1;
  }
`;

// Way back to the section list — tinted so it reads as a button, not a label.
const SectionsButton = styled.button`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45em;
  flex: ${p => (p.$block ? 1 : 'none')};
  min-width: 0;
  min-height: ${p => (p.$block ? '48px' : '40px')};
  width: ${p => (p.$icon ? '40px' : 'auto')};
  padding: ${p => (p.$icon ? '0' : '0 1em')};
  border-radius: ${p => (p.$block ? '8px' : '20px')};
  border: 1.5px solid ${GREEN};
  background: #eaf6ee;
  color: ${GREEN};
  font-weight: 700;
  font-size: 14.5px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.12s, transform 0.08s;
  .anticon {
    font-size: ${p => (p.$icon ? '18px' : '16px')};
  }
  &:hover {
    background: #dcf0e3;
  }
  &:active {
    transform: scale(0.96);
  }
  &:focus-visible {
    outline: 2px solid ${INK};
    outline-offset: 2px;
  }
`;

const IconLink = styled.button`
  all: unset;
  cursor: pointer;
  color: ${p => p.$color || MUTED};
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  /* Bigger tap area without changing the layout. */
  padding: 8px 6px;
  margin: -8px -6px;
  &:focus-visible {
    outline: 2px solid ${GREEN};
    border-radius: 4px;
  }
`;

const Nav = styled.div`
  display: flex;
  gap: 0.6em;
  margin-top: 0.9em;
  .ant-btn {
    flex: 1;
    min-height: 48px;
    font-size: 15px;
  }
`;

const Footer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  background: #fff;
  box-shadow: 0 -6px 18px rgba(20, 40, 28, 0.1);
  padding: 0.75em 1em calc(0.75em + env(safe-area-inset-bottom));

  .inner {
    max-width: 46em;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 1em;
  }
  .progress {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: ${MUTED};
  }

  /* On desktop a full-width fixed bar would sit over the sidebar, so it
     becomes the last block on the page instead. */
  @media (min-width: 992px) {
    position: static;
    background: transparent;
    box-shadow: none;
    padding: 0.75em 0 1em;
  }
`;

// Phone cameras produce 4000px images; 1280px is plenty to prove a buffet is
// stocked and keeps uploads quick on lodge Wi-Fi. 'jpeg' (not 'JPG') is what
// produces a real image/jpeg data URL.
const resizePhoto = file =>
  new Promise(resolve => {
    Resizer.imageFileResizer(file, 1280, 1280, 'jpeg', 72, 0, uri => resolve(uri), 'base64');
  });

const MARK_ALL_TOAST = 'checklist-mark-all';

const formatTime = value => (value ? dayjs(value).format('HH:mm') : '');

const RunView = ({
  run,
  user = {},
  now,
  onBack,
  onSave,
  onSaveMany,
  onComplete,
  onRefresh,
  assignees = [],
  onReassign
}) => {
  const allSections = run.sections || [];
  const oversees = isOversight(user);
  // Staff only work through their own sections; managers see everything.
  const sections = allSections.filter(s => canFillSection(run, s, user));
  const othersSections = allSections.filter(s => !sections.includes(s));

  // Answers shown immediately while they save, so tapping never waits on the
  // network. Removed when the server confirms, or reverted if it fails.
  const [pending, setPending] = React.useState({});
  const [saving, setSaving] = React.useState({});
  const [drafts, setDrafts] = React.useState({});
  const [problems, setProblems] = React.useState({});
  const [infoOpen, setInfoOpen] = React.useState({});
  // A single-section checklist skips the overview entirely.
  const [activeKey, setActiveKey] = React.useState(() =>
    sections.length === 1 ? sections[0].key : null
  );
  const [hideDone, setHideDone] = React.useState(false);
  // Items answered since entering this section stay visible even with "Show
  // what's left" on, so a mis-tap can still be corrected.
  const [touched, setTouched] = React.useState({});
  const [signOff, setSignOff] = React.useState({ open: false, name: user.name || '' });
  const [completing, setCompleting] = React.useState(false);
  const [markingAll, setMarkingAll] = React.useState(false);
  const fileInputs = React.useRef({});
  const shellRef = React.useRef(null);
  const responsesRef = React.useRef({});

  const readOnly = run.status === 'done';
  const responses = { ...(run.responses || {}), ...pending };
  responsesRef.current = responses;
  const effectiveRun = { ...run, responses };
  // Progress over the sections this person is doing.
  const progress = runProgress({ ...effectiveRun, sections });
  const mineKeys = new Set(allItems({ sections }).map(i => i.key));
  const waitingOn = othersSections.filter(s => !sectionProgress(s, responses).done);
  const [assigning, setAssigning] = React.useState(null);
  const [savingAssign, setSavingAssign] = React.useState(false);
  const state = runState(run, now());
  const anySaving = Object.values(saving).some(Boolean) || markingAll;
  const sectionIndex = sections.findIndex(s => s.key === activeKey);
  const section = sectionIndex >= 0 ? sections[sectionIndex] : null;

  // Scrolls whichever element actually scrolls (the window on phones, the
  // content pane on desktop).
  const scrollToTop = () => {
    if (shellRef.current) shellRef.current.scrollIntoView({ block: 'start' });
  };

  const goTo = key => {
    setActiveKey(key);
    setTouched({});
    scrollToTop();
  };

  const clearProblem = key =>
    setProblems(p => {
      if (!p[key]) return p;
      const next = { ...p };
      delete next[key];
      return next;
    });

  const save = async (item, response) => {
    const key = item.key;
    setTouched(t => ({ ...t, [key]: true }));
    setPending(p => ({ ...p, [key]: response ? { ...response, byName: user.name, at: new Date() } : undefined }));
    setSaving(s => ({ ...s, [key]: true }));
    clearProblem(key);
    try {
      await onSave(run._id, key, response);
    } catch (err) {
      message.error((err && err.message) || 'Could not save. Check your connection.');
      // Someone else finished this checklist in the meantime.
      if (err && err.status === 409 && onRefresh) onRefresh();
    } finally {
      setPending(p => {
        const next = { ...p };
        delete next[key];
        return next;
      });
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const current = key => responses[key] || {};

  const chooseTick = (item, status) => {
    const r = current(item.key);
    // Tapping the selected answer again clears it, for quick corrections.
    if (r.status === status) return save(item, null);
    return save(item, { status, note: r.note || '', photo: r.photo || null });
  };

  const commitReading = (item, rawValue) => {
    const r = current(item.key);
    if (rawValue === null || rawValue === undefined || rawValue === '') return null;
    if (Number(rawValue) === r.value && r.status !== 'na') return null;
    return save(item, { value: rawValue, note: r.note || '', photo: r.photo || null });
  };

  const commitText = (item, value) => {
    const r = current(item.key);
    const text = String(value || '').trim();
    if (text === (r.value || '')) return null;
    if (!text) return save(item, null);
    return save(item, { value: text, note: r.note || '', photo: r.photo || null });
  };

  const commitNote = (item, note) => {
    const r = current(item.key);
    if (!isAnswered(r) || String(note || '') === String(r.note || '')) return null;
    return save(item, { status: r.status, value: r.value, note, photo: r.photo || null });
  };

  const pickPhoto = async (item, file) => {
    if (!file) return;
    const r = current(item.key);
    if (item.type !== 'tick' && !isAnswered(r)) {
      message.info('Enter the answer first, then add the photo.');
      return;
    }
    const dataUrl = await resizePhoto(file);
    // A proof photo on an unanswered tick item means it's been done.
    save(item, { status: r.status || 'yes', value: r.value, note: r.note || '', photo: dataUrl });
  };

  const removePhoto = item => {
    const r = current(item.key);
    save(item, { status: r.status, value: r.value, note: r.note || '', photo: null });
  };

  // Only clears answers still as "All good" left them, so anything changed by
  // hand since is kept.
  const undoMarkAll = keys => {
    message.destroy(MARK_ALL_TOAST);
    const latest = responsesRef.current;
    allItems(run)
      .filter(i => keys.includes(i.key) && latest[i.key] && latest[i.key].bulk && latest[i.key].status === 'yes')
      .forEach(item => save(item, null));
  };

  const markAll = async () => {
    if (!section) return;
    const keys = markAllCandidates(section, responses);
    if (!keys.length) return;

    const stamp = { status: 'yes', bulk: true, byName: user.name, at: new Date() };
    setMarkingAll(true);
    setPending(p => keys.reduce((acc, k) => ({ ...acc, [k]: stamp }), { ...p }));
    setTouched(t => keys.reduce((acc, k) => ({ ...acc, [k]: true }), { ...t }));
    try {
      await onSaveMany(run._id, keys, 'yes');
      message.success({
        key: MARK_ALL_TOAST,
        content: (
          <span>
            Marked {keys.length} as Yes — change any that aren't.{' '}
            <IconLink $color={GREEN} onClick={() => undoMarkAll(keys)}>
              <strong>Undo</strong>
            </IconLink>
          </span>
        ),
        duration: 6
      });
    } catch (err) {
      message.error((err && err.message) || 'Could not save. Check your connection.');
    } finally {
      setPending(p => {
        const next = { ...p };
        keys.forEach(k => delete next[k]);
        return next;
      });
      setMarkingAll(false);
    }
  };

  const sectionOf = key => sections.find(s => (s.items || []).some(i => i.key === key));

  const ownerLabel = s => assigneeNames(sectionOwnerList(run, s)) || 'anyone';

  // When only other people's sections are left, there's nothing for this
  // person to fix — tell them who it's waiting on instead.
  const showWaiting = list => {
    const keys = new Set(list.map(p => p.key));
    const pendingSections = othersSections.filter(s => (s.items || []).some(i => keys.has(i.key)));
    Modal.info({
      title: 'Your part is done',
      content: (
        <div>
          <p style={{ marginBottom: '0.5em' }}>
            The checklist can be completed once these are finished:
          </p>
          <ul style={{ paddingLeft: '1.2em', margin: 0 }}>
            {pendingSections.map(s => (
              <li key={s.key}>
                <strong>{s.title}</strong> — {ownerLabel(s)}
              </li>
            ))}
          </ul>
        </div>
      ),
      okText: 'OK'
    });
  };

  const highlight = all => {
    const list = all.filter(p => mineKeys.has(p.key));
    if (!list.length) {
      if (all.length) showWaiting(all);
      return;
    }
    const map = {};
    list.forEach(p => {
      map[p.key] = map[p.key] ? `${map[p.key]} · ${p.reason}` : p.reason;
    });
    setProblems(map);

    // Take them straight to the first thing to fix.
    const target = list[0] && sectionOf(list[0].key);
    if (target) {
      setActiveKey(target.key);
      setTouched({});
      setTimeout(() => {
        const el = document.getElementById(`item-${list[0].key}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
    message.warning(`${list.length} item${list.length === 1 ? '' : 's'} still need attention`);
  };

  const finish = async signedOffName => {
    setCompleting(true);
    try {
      await onComplete(run._id, signedOffName);
      setSignOff(s => ({ ...s, open: false }));
      message.success('Checklist complete');
    } catch (err) {
      setSignOff(s => ({ ...s, open: false }));
      if (err && err.problems && err.problems.length) highlight(err.problems);
      else message.error((err && err.message) || 'Could not complete the checklist.');
    } finally {
      setCompleting(false);
    }
  };

  const handleComplete = () => {
    if (anySaving) {
      message.info('Still saving your last answer — one moment.');
      return;
    }
    const outstanding = outstandingItems(effectiveRun);
    if (outstanding.length) {
      highlight(outstanding);
      return;
    }
    if (run.requiresSignOff) setSignOff({ open: true, name: signOff.name || user.name || '' });
    else finish();
  };

  const sectionHasProblem = s => (s.items || []).some(i => problems[i.key]);

  // ---- Item ---------------------------------------------------------------

  const renderControls = item => {
    const r = current(item.key);

    if (item.type === 'reading') {
      const draft = drafts[item.key];
      const shown = draft !== undefined ? draft : r.value;
      const pass = r.status === 'na' ? null : readingPasses(item, shown);
      return (
        <div className="controls">
          {pass === true && <CheckCircleFilled style={{ color: GREEN, fontSize: 18 }} />}
          {pass === false && <ExclamationCircleFilled style={{ color: RED, fontSize: 18 }} />}
          <InputNumber
            inputMode="decimal"
            value={shown}
            // A critical item marked N/A before it became critical must still
            // be correctable, since its N/A button is gone.
            disabled={readOnly || (r.status === 'na' && !item.critical)}
            addonAfter={item.unit || undefined}
            placeholder="—"
            onChange={v => setDrafts(d => ({ ...d, [item.key]: v }))}
            onBlur={() => commitReading(item, drafts[item.key])}
            onPressEnter={() => commitReading(item, drafts[item.key])}
            size="large"
            style={{ width: item.unit ? '8.5em' : '6em' }}
            aria-label={`Reading for ${item.label}`}
          />
          {!item.critical && (
            <Dot
              $small
              $color={MUTED}
              $selected={r.status === 'na'}
              disabled={readOnly}
              aria-pressed={r.status === 'na'}
              aria-label="Not applicable"
              onClick={() => (r.status === 'na' ? save(item, null) : save(item, { status: 'na' }))}
            >
              N/A
            </Dot>
          )}
        </div>
      );
    }

    if (item.type === 'text') return null;

    return (
      <div className="controls" role="group" aria-label={item.label}>
        <Dot
          $color={GREEN}
          $selected={r.status === 'yes'}
          disabled={readOnly}
          aria-pressed={r.status === 'yes'}
          aria-label="Yes"
          onClick={() => chooseTick(item, 'yes')}
        >
          <CheckOutlined />
        </Dot>
        <Dot
          $color={RED}
          $selected={r.status === 'no'}
          disabled={readOnly}
          aria-pressed={r.status === 'no'}
          aria-label="No"
          onClick={() => chooseTick(item, 'no')}
        >
          <CloseOutlined />
        </Dot>
        {!item.critical && (
          <Dot
            $small
            $color={MUTED}
            $selected={r.status === 'na'}
            disabled={readOnly}
            aria-pressed={r.status === 'na'}
            aria-label="Not applicable"
            onClick={() => chooseTick(item, 'na')}
          >
            N/A
          </Dot>
        )}
      </div>
    );
  };

  const renderItem = item => {
    const r = current(item.key);
    const noteKey = `${item.key}__note`;
    const note = drafts[noteKey] !== undefined ? drafts[noteKey] : r.note || '';
    const needsNote = r.status === 'no';
    const photoSrc = r.photo ? (String(r.photo).startsWith('data:') ? r.photo : photoUrl(r.photo)) : null;
    const showPhoto = item.photoRequired || photoSrc;
    const range = item.type === 'reading' ? describeRange(item) : '';
    const showBy = isAnswered(r) && (readOnly || r.status === 'no' || r.bulk);

    return (
      <Row key={item.key} id={`item-${item.key}`} className={problems[item.key] ? 'problem' : ''}>
        <div className="main">
          <div className="text">
            <div className="label">{item.label}</div>
            {(range || item.responsible || item.photoRequired || item.standard || item.critical) && (
              <div className="meta">
                {item.critical && (
                  <span className="critical">
                    <WarningFilled /> Critical
                  </span>
                )}
                {range && <span>{range}</span>}
                {item.responsible && <span>{item.responsible}</span>}
                {item.photoRequired && (
                  <span style={{ color: r.photo ? GREEN : AMBER }}>
                    {r.photo ? <CameraFilled /> : <CameraOutlined />} Photo
                  </span>
                )}
                {item.standard && (
                  <IconLink
                    aria-expanded={Boolean(infoOpen[item.key])}
                    onClick={() => setInfoOpen(o => ({ ...o, [item.key]: !o[item.key] }))}
                  >
                    <InfoCircleOutlined /> {infoOpen[item.key] ? 'Hide' : 'Standard'}
                  </IconLink>
                )}
              </div>
            )}
          </div>
          {renderControls(item)}
        </div>

        {infoOpen[item.key] && item.standard && <div className="standard">{item.standard}</div>}

        {item.type === 'text' && (
          <div className="extra">
            <TextArea
              size="large"
              autoSize={{ minRows: 1, maxRows: 5 }}
              value={drafts[item.key] !== undefined ? drafts[item.key] : r.value || ''}
              disabled={readOnly}
              placeholder="Type your answer"
              onChange={e => {
                const value = e.target.value;
                setDrafts(d => ({ ...d, [item.key]: value }));
              }}
              onBlur={() => commitText(item, drafts[item.key])}
            />
          </div>
        )}

        {(needsNote || (r.note && isAnswered(r))) && (
          <div className="extra">
            <TextArea
              size="large"
              autoSize={{ minRows: 1, maxRows: 4 }}
              value={note}
              disabled={readOnly}
              status={needsNote && !String(note).trim() ? 'warning' : undefined}
              placeholder={item.type === 'reading' ? 'Corrective action taken' : 'What is wrong?'}
              onChange={e => {
                const value = e.target.value;
                setDrafts(d => ({ ...d, [noteKey]: value }));
              }}
              onBlur={() => commitNote(item, drafts[noteKey])}
            />
          </div>
        )}

        {showPhoto && (
          <div className="extra" style={{ display: 'flex', alignItems: 'center', gap: '0.6em', flexWrap: 'wrap' }}>
            {photoSrc && (
              <Image
                src={photoSrc}
                width={72}
                height={72}
                style={{ borderRadius: 10, objectFit: 'cover' }}
                alt={`Photo for ${item.label}`}
              />
            )}
            {!readOnly && (
              <>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  ref={el => {
                    fileInputs.current[item.key] = el;
                  }}
                  onChange={e => {
                    const file = e.target.files && e.target.files[0];
                    e.target.value = '';
                    pickPhoto(item, file);
                  }}
                />
                <PhotoButton
                  type="button"
                  $retake={Boolean(photoSrc)}
                  onClick={() => fileInputs.current[item.key] && fileInputs.current[item.key].click()}
                >
                  <CameraFilled />
                  {photoSrc ? 'Retake photo' : 'Take photo'}
                </PhotoButton>
                {photoSrc && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removePhoto(item)}
                    aria-label="Remove photo"
                    style={{ height: 44, width: 44 }}
                  />
                )}
              </>
            )}
          </div>
        )}

        {problems[item.key] && <div className="reason">{problems[item.key]}</div>}
        {saving[item.key] ? (
          <div className="by">
            <Spin size="small" /> Saving…
          </div>
        ) : (
          showBy && (
            <div className="by">
              {r.byName}
              {r.at ? ` · ${formatTime(r.at)}` : ''}
              {r.bulk ? ' · via All good' : ''}
            </div>
          )
        )}
      </Row>
    );
  };

  // ---- Views --------------------------------------------------------------

  const renderOverview = () => {
    const visible = hideDone
      ? sections.filter(s => !sectionProgress(s, responses).done || sectionHasProblem(s))
      : sections;
    return (
      <>
        {visible.length ? (
          <SectionList>
            {visible.map(s => {
              const sp = sectionProgress(s, responses);
              const flagged = sectionHasProblem(s);
              return (
                <SectionRow key={s.key} {...pressable(() => goTo(s.key))}>
                  {flagged ? (
                    <ExclamationCircleFilled style={{ color: RED, fontSize: 22 }} />
                  ) : sp.done ? (
                    <CheckCircleFilled style={{ color: GREEN, fontSize: 22 }} />
                  ) : (
                    <Progress
                      type="circle"
                      percent={sp.total ? Math.round((sp.answered / sp.total) * 100) : 0}
                      size={22}
                      strokeColor={GREEN}
                      showInfo={false}
                    />
                  )}
                  <span className="title">
                    {s.title}
                    {oversees && (s.assignees || []).length > 0 && (
                      <span className="owner">
                        <TeamOutlined /> {assigneeNames(s.assignees)}
                      </span>
                    )}
                  </span>
                  <span className="count">
                    {sp.answered}/{sp.total}
                  </span>
                  <RightOutlined style={{ color: MUTED }} />
                </SectionRow>
              );
            })}
          </SectionList>
        ) : (
          <Banner $tone="done">
            <CheckCircleFilled />{' '}
            {waitingOn.length
              ? 'Your part is done.'
              : 'Every section is done — press Complete to finish.'}
          </Banner>
        )}

        {othersSections.length > 0 && (
          <>
            <Note style={{ padding: '1.1em 0.25em 0.45em' }}>Done by others</Note>
            <SectionList>
              {othersSections.map(s => {
                const op = sectionProgress(s, responses);
                return (
                  <SectionRow key={s.key} style={{ cursor: 'default', background: 'none' }}>
                    {op.done ? (
                      <CheckCircleFilled style={{ color: GREEN, fontSize: 22 }} />
                    ) : (
                      <TeamOutlined style={{ color: MUTED, fontSize: 20 }} />
                    )}
                    <span className="title">
                      {s.title}
                      <span className="owner">{ownerLabel(s)}</span>
                    </span>
                    <span className="count">
                      {op.answered}/{op.total}
                    </span>
                  </SectionRow>
                );
              })}
            </SectionList>
          </>
        )}
      </>
    );
  };

  // ---- Assigning (managers, today's run only) -----------------------------

  const people = new Map(assignees.map(a => [a.id, a.name]));
  [run.assignees, ...allSections.map(s => s.assignees)].forEach(list =>
    (list || []).forEach(a => {
      if (a && a.id && !people.has(a.id)) people.set(a.id, a.name || 'Former user');
    })
  );
  const peopleOptions = Array.from(people, ([value, label]) => ({ value, label }));

  const openAssign = () =>
    setAssigning({
      assignees: assigneeIds(run.assignees),
      sections: allSections.reduce((acc, s) => ({ ...acc, [s.key]: assigneeIds(s.assignees) }), {})
    });

  const saveAssign = async () => {
    setSavingAssign(true);
    try {
      await onReassign(run._id, assigning.assignees, assigning.sections);
      setAssigning(null);
      message.success('Assigned for today');
    } catch (err) {
      message.error((err && err.message) || 'Could not reassign the checklist.');
    } finally {
      setSavingAssign(false);
    }
  };

  const renderAssign = () => {
    const checklistNames = assigning.assignees.map(id => people.get(id)).filter(Boolean).join(', ');
    return (
      <Modal
        open
        title="Assign today's checklist"
        okText="Save"
        confirmLoading={savingAssign}
        okButtonProps={{ style: { backgroundColor: GREEN } }}
        onOk={saveAssign}
        onCancel={() => setAssigning(null)}
      >
        <p style={{ color: MUTED, marginTop: 0 }}>
          Only changes today's checklist, e.g. when someone is off. To change it
          every day, edit the checklist under Templates.
        </p>
        <div style={{ fontWeight: 700, marginBottom: '0.35em' }}>Whole checklist</div>
        <Select
          mode="multiple"
          size="large"
          allowClear
          showSearch
          optionFilterProp="label"
          value={assigning.assignees}
          options={peopleOptions}
          placeholder="Everyone at the site"
          onChange={ids => setAssigning(a => ({ ...a, assignees: ids }))}
          style={{ width: '100%' }}
        />
        {allSections.length > 1 && (
          <>
            <div style={{ fontWeight: 700, margin: '1.1em 0 0.35em' }}>Sections (optional)</div>
            {allSections.map(s => (
              <div key={s.key} style={{ marginBottom: '0.6em' }}>
                <div style={{ fontSize: 13, color: MUTED, marginBottom: '0.2em' }}>{s.title}</div>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={assigning.sections[s.key] || []}
                  options={peopleOptions}
                  placeholder={
                    checklistNames ? `Same as checklist (${checklistNames})` : 'Same as checklist (everyone)'
                  }
                  onChange={ids =>
                    setAssigning(a => ({ ...a, sections: { ...a.sections, [s.key]: ids } }))
                  }
                  style={{ width: '100%' }}
                />
              </div>
            ))}
          </>
        )}
      </Modal>
    );
  };

  const renderSection = () => {
    const candidates = markAllCandidates(section, responses);
    const visibleItems = (section.items || []).filter(
      item =>
        !hideDone ||
        itemNeedsAttention(item, responses[item.key]) ||
        touched[item.key] ||
        problems[item.key]
    );
    const prev = sections[sectionIndex - 1];
    const next = sections[sectionIndex + 1];

    return (
      <>
        <Card>
          {!readOnly && run.allowMarkAll !== false && candidates.length > 0 && (
            <MarkAll>
              <span className="hint">
                {candidates.length} item{candidates.length === 1 ? '' : 's'} left to tick here
              </span>
              <Button
                size="large"
                type="primary"
                icon={<CheckOutlined />}
                loading={markingAll}
                onClick={markAll}
                style={{ backgroundColor: GREEN }}
              >
                All good
              </Button>
            </MarkAll>
          )}
          {section.note && <Note>{section.note}</Note>}
          {visibleItems.length ? (
            visibleItems.map(renderItem)
          ) : (
            <div style={{ padding: '1em', color: GREEN, fontWeight: 600 }}>
              <CheckCircleFilled /> Everything in this section is done.
            </div>
          )}
        </Card>

        {sections.length > 1 && (
          <Nav>
            {prev ? (
              <SectionsButton type="button" $block onClick={() => goTo(prev.key)}>
                <ArrowLeftOutlined />
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {prev.title}
                </span>
              </SectionsButton>
            ) : (
              <SectionsButton type="button" $block onClick={() => goTo(null)}>
                <UnorderedListOutlined /> All sections
              </SectionsButton>
            )}
            {next ? (
              <Button type="primary" onClick={() => goTo(next.key)} style={{ backgroundColor: INK }}>
                {next.title} <ArrowRightOutlined />
              </Button>
            ) : (
              <Button type="primary" onClick={() => goTo(null)} style={{ backgroundColor: INK }}>
                Review all sections
              </Button>
            )}
          </Nav>
        )}
      </>
    );
  };

  const subtitle = runSubtitle(run);
  const sp = section ? sectionProgress(section, responses) : null;

  return (
    <Shell ref={shellRef}>
      <Head>
        <div className="row">
          <BackButton onClick={onBack} aria-label="Back to today" />
          <div className="text">
            <h1 className="name">{run.templateName}</h1>
            <div className="sub">
              {subtitle && `${subtitle} · `}
              <span style={{ color: TONE_COLOR[state.tone], fontWeight: 600 }}>{state.label}</span>
              {oversees && assigneeNames(run.assignees) && (
                <>
                  {' · '}
                  <TeamOutlined /> {assigneeNames(run.assignees)}
                </>
              )}
            </div>
          </div>
          {onReassign && !readOnly && (
            <Button
              icon={<TeamOutlined />}
              onClick={openAssign}
              style={{ height: 40, flex: 'none' }}
            >
              Assign
            </Button>
          )}
        </div>
        <Progress
          percent={progress.percent}
          showInfo={false}
          strokeColor={GREEN}
          size="small"
          style={{ margin: '0.3em 0 0' }}
        />
      </Head>

      <SubBar>
          {section && sections.length > 1 ? (
            <SectionsButton type="button" $icon onClick={() => goTo(null)} aria-label="All sections" title="All sections">
              <UnorderedListOutlined />
            </SectionsButton>
          ) : null}
          <span className="crumb">
            {section ? (
              <>
                {section.title}{' '}
                <span style={{ color: MUTED, fontWeight: 500 }}>
                  {sp.answered}/{sp.total}
                </span>
              </>
            ) : (
              <span style={{ color: MUTED, fontWeight: 500 }}>
                {sections.length} sections · {progress.answered} of {progress.total} done
              </span>
            )}
          </span>
          {!readOnly && (
            <label className="toggle">
              <Switch checked={hideDone} onChange={setHideDone} /> Show what's left
            </label>
          )}
      </SubBar>

      {readOnly ? (
        <Banner $tone="done">
          <CheckCircleFilled /> Completed by <strong>{run.completedByName}</strong> at{' '}
          {formatTime(run.completedAt)}
          {run.signedOffName && run.signedOffName !== run.completedByName
            ? ` · signed off by ${run.signedOffName}`
            : ''}
          {run.late && (
            <Tag color="orange" style={{ marginLeft: '0.5em' }}>
              {state.label}
            </Tag>
          )}
        </Banner>
      ) : (
        !section && run.description && <Banner>{run.description}</Banner>
      )}

      {section ? renderSection() : renderOverview()}

      {!readOnly && (
        <Footer>
          <div className="inner">
            <div className="progress">
              {progress.answered === progress.total && waitingOn.length ? (
                <>
                  <strong style={{ color: GREEN }}>Your part is done</strong>
                  <br />
                  Waiting on {waitingOn.map(s => s.title).join(', ')}
                </>
              ) : (
                <>
                  <strong style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>
                    {progress.answered} of {progress.total}
                  </strong>{' '}
                  done
                </>
              )}
            </div>
            <Button
              type="primary"
              size="large"
              loading={completing}
              onClick={handleComplete}
              style={{ backgroundColor: progress.answered === progress.total ? GREEN : INK, minWidth: '9em' }}
            >
              Complete
            </Button>
          </div>
        </Footer>
      )}

      {assigning && renderAssign()}

      <Modal
        open={signOff.open}
        title="Sign off this checklist"
        okText="Sign off & complete"
        confirmLoading={completing}
        okButtonProps={{ disabled: !String(signOff.name || '').trim(), style: { backgroundColor: GREEN } }}
        onOk={() => finish(signOff.name)}
        onCancel={() => setSignOff(s => ({ ...s, open: false }))}
      >
        <p style={{ color: MUTED }}>
          By signing off you confirm every item on this checklist meets the
          standard, or has been noted where it doesn't.
        </p>
        <Input
          size="large"
          value={signOff.name}
          placeholder="Your name"
          onChange={e => {
            const value = e.target.value;
            setSignOff(s => ({ ...s, name: value }));
          }}
        />
      </Modal>
    </Shell>
  );
};

export default RunView;
